import React, { useState, useRef, useEffect } from "react";
import SC from "./style";
import InnoIcons from "@InnoIcons/";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import theme from "@theme/";

const AlarmDateDropdown = ({
  label = "Alarm Date",
  value = null,
  onUpdate,
}) => {
  const dropdownRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("BETWEEN");
  const [values, setValues] = useState({
    inRangeFrom: "",
    inRangeTo: "",
    sent: "0",
  });

  useEffect(() => {
    if (!value) {
      setValues({ inRangeFrom: "", inRangeTo: "", sent: "0" });
      setSelectedOption("BETWEEN");
      return;
    }

    const { header, value: val, sent } = value;
    if (header === "BETWEEN" && Array.isArray(val)) {
      setSelectedOption("BETWEEN");
      setValues({ inRangeFrom: val[0], inRangeTo: val[1], sent: sent ?? "0" });
    } else {
      setValues({ inRangeFrom: "", inRangeTo: "", sent: sent ?? "0" });
      setSelectedOption(header || "BETWEEN");
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleValueChange = (key, val) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleUpdate = () => {
    const { inRangeFrom, inRangeTo, sent } = values;
    if (selectedOption === "BETWEEN" && inRangeFrom && inRangeTo) {
      onUpdate?.({ header: "BETWEEN", value: [inRangeFrom, inRangeTo], sent });
    } else {
      onUpdate?.({ header: selectedOption, value: null, sent });
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setValues({ inRangeFrom: "", inRangeTo: "", sent: "0" });
    onUpdate?.(null);
    setIsOpen(false);
  };

  const hasValidValue = values.inRangeFrom && values.inRangeTo;

  const options = [
    { key: "1", label: "Yes" },
    { key: "0", label: "No" },
  ];

  return (
    <SC.DropdownContainer ref={dropdownRef}>
      <SC.DropdownButton
        isOpen={isOpen}
        isActive={hasValidValue}
        onClick={() => setIsOpen(!isOpen)}
      >
        {hasValidValue
          ? `${label}: ${values.inRangeFrom} to ${values.inRangeTo} (Sent: ${values.sent})`
          : label}
        <SC.ChevronIcon size={16} isOpen={isOpen} isActive={hasValidValue} />
      </SC.DropdownButton>

      <SC.DropdownMenu isOpen={isOpen}>
        <SC.OptionGroup>
          <span className="section-label">Between:</span>
          {selectedOption === "BETWEEN" && (
            <SC.InputRow>
              <SC.DateInput
                type="date"
                value={values.inRangeFrom}
                onChange={(e) =>
                  handleValueChange("inRangeFrom", e.target.value)
                }
              />
              <SC.DateSeparator>to</SC.DateSeparator>
              <SC.DateInput
                type="date"
                value={values.inRangeTo}
                onChange={(e) => handleValueChange("inRangeTo", e.target.value)}
              />
            </SC.InputRow>
          )}

          <SC.RadioRow>
            <span className="section-label">Sent:</span>
            <RadioGroup
              row
              value={values.sent}
              onChange={(e) => handleValueChange("sent", e.target.value)}
              sx={{ display: "flex", gap: "20px", alignItems: "center" }}
            >
              {options.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  value={opt.key}
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: theme.colors.themeColor },
                      }}
                    />
                  }
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </SC.RadioRow>
        </SC.OptionGroup>

        <SC.DropdownFooter>
          <SC.UpdateButton onClick={handleUpdate}>Update</SC.UpdateButton>
          <SC.ClearButton onClick={handleClear}>Clear</SC.ClearButton>
        </SC.DropdownFooter>
      </SC.DropdownMenu>
    </SC.DropdownContainer>
  );
};

export default AlarmDateDropdown;
